import { RotateLeft, RotateRight, ZoomIn, ZoomOut } from '@mui/icons-material'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { GreenText } from 'components'
import { useEffect, useRef, useState } from 'react'

interface ViewPhotoDialogProps {
  fileName: string
  greenText?: string
  onClose: () => void
  open: boolean
}

export default function ViewTitleApplicationDeliveryPhotoDialog({ fileName, greenText, onClose, open }: ViewPhotoDialogProps) {
  const [rotation, setRotation] = useState<number>(0)
  const [zoom, setZoom] = useState<number>(1)
  const [isDragging, setIsDragging] = useState(false)
  const [originX, setOriginX] = useState(0)
  const [originY, setOriginY] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const rotateLeft = () => setRotation(prev => prev - 90)
  const rotateRight = () => setRotation(prev => prev + 90)
  const zoomIn = () => setZoom(prev => prev + 0.2)
  const zoomOut = () => setZoom(prev => Math.max(1, prev - 0.2))

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDragging) {
      const deltaX = e.clientX - originX
      const deltaY = e.clientY - originY
      let newTranslateX = translateX + deltaX
      let newTranslateY = translateY + deltaY

      if (containerRef.current && imageRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()

        const imageWidth = imageRef.current.offsetWidth * zoom
        const imageHeight = imageRef.current.offsetHeight * zoom

        const maxTranslateX = (imageWidth - containerRect.width) / 2
        const maxTranslateY = (imageHeight - containerRect.height) / 2

        const padding = 100

        newTranslateX = Math.max(-maxTranslateX - padding, Math.min(newTranslateX, maxTranslateX + padding))
        newTranslateY = Math.max(-maxTranslateY - padding, Math.min(newTranslateY, maxTranslateY + padding))

        setTranslateX(newTranslateX)
        setTranslateY(newTranslateY)
      }

      setTranslateX(newTranslateX)
      setTranslateY(newTranslateY)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    setRotation(0)
    setZoom(1)
    setTranslateX(0)
    setTranslateY(0)
  }, [fileName])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogContent>
        <Grid container spacing={2} alignItems='center'>
          <DialogTitle>View Photo</DialogTitle>

          <Grid item xs={12}>
            <div
              ref={containerRef}
              style={{
                overflow: 'hidden',
                width: '100%',
                height: '60vh',
                backgroundColor: 'black',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                borderRadius: '8px'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                ref={imageRef}
                src={`/api/documents/${encodeURIComponent(fileName)}/preview`}
                alt='Preview'
                style={{
                  width: '80%',
                  height: '100%',
                  transform: `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: 'center center',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  transition: isDragging ? 'none' : 'transform 0.3s ease'
                }}
                draggable={false}
              />
            </div>

            <Grid container justifyContent='space-between' alignItems='center'>
              <Grid item ml={1.25}>
                <Button endIcon={<ZoomIn />} color='primary' onClick={zoomIn} variant='text'>
                  ZOOM IN
                </Button>
              </Grid>

              <Grid item mr={1.25}>
                <Button endIcon={<ZoomOut />} color='primary' onClick={zoomOut} variant='text'>
                  ZOOM OUT
                </Button>
              </Grid>
            </Grid>

            <Grid container xs={12} margin='auto' mt={1} justifyContent='center' alignItems='center'>
              <Grid item xs={4} sm='auto'>
                <Button startIcon={<RotateLeft />} color='primary' onClick={rotateLeft} variant='text'>
                  ROTATE LEFT
                </Button>
              </Grid>

              <Grid item xs={4}></Grid>

              <Grid item xs={4} sm='auto'>
                <Button endIcon={<RotateRight />} color='primary' onClick={rotateRight} variant='text'>
                  ROTATE RIGHT
                </Button>
              </Grid>
            </Grid>
          </Grid>

          {greenText && (
            <Grid item xs={12}>
              <GreenText variant='outlined'>{greenText}</GreenText>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
